import { useForm, DefaultValues } from 'react-hook-form';
import type {
  EmailConfigurationCreate,
  EmailConfigurationUpdate,
} from '../types/emailConfiguration.types';
import { useCallback } from 'react';
import { isEncrypted, encrypt } from '../../../../../utils/crypto.util';
import { getConfig } from '../../../../../config';

export interface UseEmailConfigurationFormProps {
  defaultValues?: DefaultValues<EmailConfigurationCreate | EmailConfigurationUpdate>;
  onSubmit: (data: EmailConfigurationCreate | EmailConfigurationUpdate) => void;
}

export const useEmailConfigurationForm = ({
  defaultValues,
  onSubmit,
}: UseEmailConfigurationFormProps) => {
  const { flightEncryptionKey } = getConfig();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
  } = useForm<EmailConfigurationCreate | EmailConfigurationUpdate>({
    defaultValues: defaultValues || {
      email: undefined,
      host: undefined,
      port: undefined,
      isSslTls: undefined,
      password: undefined,
    },
  });

  const resetForm = useCallback(() => {
    reset();
  }, [reset]);

  const submitHandler = useCallback(
    (data: EmailConfigurationCreate | EmailConfigurationUpdate) => {
      try {
        // Encrypt password if it's provided and not already encrypted
        if (data.password && !isEncrypted(data.password)) {
          data.password = encrypt(data.password, flightEncryptionKey);
        }
        onSubmit(data);
      } catch (error) {
        console.error('Failed to encrypt password:', error);
        // Still submit the form, but log the error
        onSubmit(data);
      }
    },
    [onSubmit, flightEncryptionKey]
  );

  const formSubmitHandler = useCallback(() => {
    return handleSubmit(submitHandler);
  }, [handleSubmit, submitHandler]);

  return {
    control,
    errors,
    resetForm,
    formSubmitHandler,
    reset,
    setError,
    setValue,
  };
};
